import React from 'react';
import { useTheme } from '../theme/ThemeContext';
import { useSelector, useDispatch } from 'react-redux';
import { selectSettings, updateSetting, selectUser } from '../redux/features/platformSlice';
import { UserIcon, ShieldIcon, ZapIcon, InfoIcon } from '../helpers/Icons';

const Settings = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const settings = useSelector(selectSettings);
  const user = useSelector(selectUser);
  const account = useSelector(s => s.platform.account);

  const Section = ({ title, icon: Icon, children }) => (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Icon s={16} c={theme.primary} />
        <h3 style={{ fontSize: 14, fontWeight: 700, color: theme.text, margin: 0 }}>{title}</h3>
      </div>
      <div style={{ background: theme.card2, borderRadius: 12, border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );

  const SettingRow = ({ label, desc, children }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${theme.border}44` }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: theme.text }}>{label}</div>
        {desc && <div style={{ fontSize: 10, color: theme.textM, marginTop: 2 }}>{desc}</div>}
      </div>
      {children}
    </div>
  );

  const Toggle = ({ checked, onChange }) => (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 34, height: 18, borderRadius: 10, background: checked ? theme.primary : theme.border,
        position: 'relative', cursor: 'pointer', transition: 'all 0.2s'
      }}
    >
      <div style={{
        width: 14, height: 14, borderRadius: '50%', background: '#fff',
        position: 'absolute', top: 2, left: checked ? 18 : 2, transition: 'all 0.2s'
      }} />
    </div>
  );

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: 800, color: theme.text, marginBottom: 24 }}>Settings</h2>

      <Section title="Profile Information" icon={UserIcon}>
        <SettingRow label="Display Name">
          <span style={{ fontSize: 12, color: theme.textS }}>{user?.name || 'Demo User'}</span>
        </SettingRow>
        <SettingRow label="Account ID">
          <span style={{ fontSize: 12, color: theme.textS, fontFamily: theme.mono }}>{account?.login}</span>
        </SettingRow>
        <SettingRow label="Email Address">
          <span style={{ fontSize: 12, color: theme.textS }}>{user?.email || 'demo@example.com'}</span>
        </SettingRow>
        <SettingRow label="Group">
          <span style={{ fontSize: 12, color: theme.textS }}>{user?.group_id?.group_name || 'Standard'}</span>
        </SettingRow>
      </Section>

      <Section title="Trading Preferences" icon={ZapIcon}>
        <SettingRow label="Confirm Open Order" desc="Show confirmation modal before placing a new trade">
          <Toggle checked={settings.confirmOpen} onChange={v => dispatch(updateSetting({ key: 'confirmOpen', value: v }))} />
        </SettingRow>
        <SettingRow label="Confirm Close Order" desc="Show confirmation modal before closing a position">
          <Toggle checked={settings.confirmClose} onChange={v => dispatch(updateSetting({ key: 'confirmClose', value: v }))} />
        </SettingRow>
        <SettingRow label="Price Change Flash" desc="Highlight price changes in the symbol list">
          <Toggle checked={settings.showNotifications} onChange={v => dispatch(updateSetting({ key: 'showNotifications', value: v }))} />
        </SettingRow>
      </Section>

      <Section title="Security & Notifications" icon={ShieldIcon}>
        <SettingRow label="Push Notifications" desc="Receive alerts for price movements and order status">
          <Toggle checked={true} onChange={() => { }} />
        </SettingRow>
        <SettingRow label="Two-Factor Authentication" desc="Add an extra layer of security to your account">
          <button style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${theme.border}`, background: 'transparent', color: theme.primary, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
            Enable
          </button>
        </SettingRow>
      </Section>

      <Section title="About" icon={InfoIcon}>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: theme.textM, lineHeight: 1.6 }}>
            Core Prime LTD v1.2.0<br />
            Professional Trading Platform Port<br />
            © 2026 Core Prime LTD
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Settings;
